import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Link,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Assessment as ReportIcon,
  Language as LinkIcon,
} from '@mui/icons-material';

interface XmlResponseViewerProps {
  xmlResponse: string;
}

interface EmissionsData {
  co2: string;
  co2Unit: string;
  totalEmissions: string;
  totalUnit: string;
  energy: string;
  energyUnit: string;
  distance: string;
  distanceUnit: string;
  totalEI: string;
  totalEIUnit: string;
  compensation: string;
  compensationUnit: string;
  dbId: string;
  reportUrlPdf: string;
  reportUrlHtml: string;
}

interface ParsedXmlResponse {
  shipmentId: string;
  isError: boolean;
  errorMessage: string;
  emissions: EmissionsData | null;
  success: boolean;
}

const XmlResponseViewer: React.FC<XmlResponseViewerProps> = ({ xmlResponse }) => {
  const cleanXmlForDisplay = (xmlText: string): string => {
    // Remove entire <Status>...</Status> block when IsError is false
    if (xmlText.includes('<IsError>false</IsError>')) {
      return xmlText.replaceAll(/\s{0,64}<Status>[\s\S]*?<\/Status>\s{0,64}/g, (match) => {
        if (match.includes('<IsError>false</IsError>')) {
          return '\n';
        }
        return match;
      });
    }
    return xmlText;
  };

  // Parse XML to extract key information
  const parseXmlResponse = (xmlText: string): ParsedXmlResponse => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

      // Check for parser error
      const parsererror = xmlDoc.querySelector('parsererror');
      if (parsererror) {
        return {
          shipmentId: 'N/A',
          isError: true,
          errorMessage: 'Failed to parse XML response',
          emissions: {
            co2: 'N/A',
            co2Unit: '',
            totalEmissions: 'N/A',
            totalUnit: '',
            energy: 'N/A',
            energyUnit: '',
            distance: 'N/A',
            distanceUnit: '',
            totalEI: 'N/A',
            totalEIUnit: '',
            compensation: 'N/A',
            compensationUnit: '',
            dbId: 'N/A',
            reportUrlPdf: '',
            reportUrlHtml: '',
          },
          success: false
        };
      }

      const shipmentId = xmlDoc.querySelector('Shipments')?.getAttribute('Id') || 'N/A';
      const isError = xmlDoc.querySelector('IsError')?.textContent === 'true';
      const errorMessage = xmlDoc.querySelector('ErrorMessage')?.textContent || '';

      // Extract emissions data
      const emissions: EmissionsData = {
        co2: xmlDoc.querySelector('CO2')?.textContent || xmlDoc.querySelector('OPS')?.textContent || 'N/A',
        co2Unit: xmlDoc.querySelector('CO2')?.getAttribute('Unit') || xmlDoc.querySelector('OPS')?.getAttribute('Unit') || 'kg',
        totalEmissions: xmlDoc.querySelector('TOT')?.textContent || 'N/A',
        totalUnit: xmlDoc.querySelector('TOT')?.getAttribute('Unit') || 'kg',
        energy: xmlDoc.querySelector('ENE')?.textContent || 'N/A',
        energyUnit: xmlDoc.querySelector('ENE')?.getAttribute('Unit') || 'kg',
        distance: xmlDoc.querySelector('TKM')?.textContent || 'N/A',
        distanceUnit: xmlDoc.querySelector('TKM')?.getAttribute('Unit') || 'TKM',
        totalEI: xmlDoc.querySelector('TOT_EI')?.textContent || 'N/A',
        totalEIUnit: xmlDoc.querySelector('TOT_EI')?.getAttribute('Unit') || 'gr/tkm',
        compensation: xmlDoc.querySelector('Compensation')?.textContent || 'N/A',
        compensationUnit: xmlDoc.querySelector('Compensation')?.getAttribute('Unit') || 'CHF',
        dbId: xmlDoc.querySelector('DbId')?.textContent || 'N/A',
        reportUrlPdf: xmlDoc.querySelector('ReportUrl')?.textContent || '',
        reportUrlHtml: xmlDoc.querySelector('ReportUrlHTML')?.textContent || '',
      };

      return {
        shipmentId,
        isError,
        errorMessage,
        emissions,
        success: !isError && emissions.co2 !== 'N/A'
      };
    } catch (error) {
      console.error('Failed to parse XML response:', error);
      return {
        shipmentId: 'N/A',
        isError: true,
        errorMessage: 'Failed to parse XML response',
        emissions: null,
        success: false
      };
    }
  };

  const formatXmlWithSyntaxHighlighting = (xmlText: string) => {
    // Simple XML syntax highlighting
    return xmlText
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll(/&lt;(\/?[\w-]{1,100})([^&]{0,5000}?)&gt;/g, (match, tagName, attrs) => {
        const isClosingTag = tagName.startsWith('/');
        const cleanTagName = isClosingTag ? tagName.slice(1) : tagName;

        // Color different types of tags
        let tagColor = '#2196F3'; // Blue for regular tags
        if (['CarbonCareApi', 'Response', 'Shipments'].includes(cleanTagName)) {
          tagColor = '#9C27B0'; // Purple for main structure
        } else if (['Emissions', 'Status'].includes(cleanTagName)) {
          tagColor = '#FF9800'; // Orange for data sections
        } else if (['OPS', 'TOT', 'ENE', 'TKM', 'TOT_EI', 'Compensation'].includes(cleanTagName)) {
          tagColor = '#4CAF50'; // Green for data values
        }

        return `<span style="color: ${tagColor}; font-weight: 600;">&lt;${tagName}</span><span style="color: #795548;">${attrs}</span><span style="color: ${tagColor}; font-weight: 600;">&gt;</span>`;
      })
      .replaceAll(/(https?:\/\/[^\s&<]+)/g, '<a href="$1" target="_blank" style="color: #1976D2; text-decoration: underline;">$1</a>')
      .replaceAll(/&gt;([^&<]+)&lt;/g, (match, content) => {
        // Color the content between tags
        if (!Number.isNaN(Number(content)) && content.trim() !== '') {
          return `&gt;<span style="color: #F44336; font-weight: 600;">${content}</span>&lt;`;
        } else if (content === 'true' || content === 'false') {
          return `&gt;<span style="color: ${content === 'true' ? '#4CAF50' : '#FF5722'}; font-weight: 600;">${content}</span>&lt;`;
        } else {
          return `&gt;<span style="color: #424242;">${content}</span>&lt;`;
        }
      });
  };

  const parsedData = parseXmlResponse(xmlResponse);
  const cleanedXml = cleanXmlForDisplay(xmlResponse);
  const formattedXml = formatXmlWithSyntaxHighlighting(cleanedXml);

  return (
    <Box>
      {/* Summary Cards */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Chip
            icon={parsedData.success ? <SuccessIcon /> : <ErrorIcon />}
            label={parsedData.success ? 'Success' : 'Error'}
            color={parsedData.success ? 'success' : 'error'}
            variant="filled"
          />
          <Chip
            label={`Shipment: ${parsedData.shipmentId}`}
            color="primary"
            variant="outlined"
          />
          {parsedData.emissions?.dbId && parsedData.emissions.dbId !== 'N/A' && (
            <Chip
              label={`DB ID: ${parsedData.emissions.dbId}`}
              color="secondary"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Emissions Summary */}
        {parsedData.success && parsedData.emissions && (
          <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #E8F5E8, #F1F8E9)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2E7D32', fontWeight: 600 }}>
              Carbon Emissions Results
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
               <Box>
                <Typography variant="body2" color="text.secondary">Total Emissions (TOT)</Typography>
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 600 }}>
                  {Number.parseFloat(parsedData.emissions.totalEmissions).toLocaleString()} {parsedData.emissions.totalUnit} CO2e
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Operational Emissions (OPS)</Typography>
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 600 }}>
                  {Number.parseFloat(parsedData.emissions.co2).toLocaleString()} {parsedData.emissions.co2Unit} CO2e
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Energy Provision (ENE)</Typography>
                <Typography variant="h6" sx={{ color: '#FF9800', fontWeight: 600 }}>
                  {Number.parseFloat(parsedData.emissions.energy).toLocaleString()} {parsedData.emissions.energyUnit} CO2e
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">TKM</Typography>
                <Typography variant="h6" sx={{ color: '#9C27B0', fontWeight: 600 }}>
                  {Number.parseFloat(parsedData.emissions.distance).toLocaleString()}
                </Typography>
              </Box>
              {parsedData.emissions.totalEI !== 'N/A' && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Emission Intensity</Typography>
                  <Typography variant="h6" sx={{ color: '#795548', fontWeight: 600 }}>
                    {Number.parseFloat(parsedData.emissions.totalEI).toFixed(2)} {parsedData.emissions.totalEIUnit}
                  </Typography>
                </Box>
              )}
              {parsedData.emissions.compensation !== 'N/A' && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Compensation Cost</Typography>
                  <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                    {Number.parseFloat(parsedData.emissions.compensation).toFixed(2)} {parsedData.emissions.compensationUnit}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {/* Report Links */}
        {(parsedData.emissions?.reportUrlPdf || parsedData.emissions?.reportUrlHtml) && (
          <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #E3F2FD, #F8F9FF)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976D2', fontWeight: 600 }}>
              <ReportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Generated Reports
            </Typography>
            <Stack direction="row" spacing={2}>
              {parsedData.emissions?.reportUrlPdf && (
                <Link
                  href={parsedData.emissions.reportUrlPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: '#1976D2',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <LinkIcon sx={{ mr: 1, fontSize: 20 }} />
                  Download PDF Report
                </Link>
              )}
              {parsedData.emissions?.reportUrlHtml && (
                <Link
                  href={parsedData.emissions.reportUrlHtml}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: '#1976D2',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <LinkIcon sx={{ mr: 1, fontSize: 20 }} />
                  View HTML Report
                </Link>
              )}
            </Stack>
          </Paper>
        )}

        {/* Error Message */}
        {parsedData.isError && parsedData.errorMessage && (
          <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #FFEBEE, #FFF8F8)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#D32F2F', fontWeight: 600 }}>
              <ErrorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Error Details
            </Typography>
            <Typography variant="body1" sx={{ color: '#D32F2F' }}>
              {parsedData.errorMessage}
            </Typography>
          </Paper>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Raw XML Response */}
      <Typography variant="h6" gutterBottom>
        Raw XML Response
      </Typography>
      <Paper
        sx={{
          p: 2,
          backgroundColor: '#FAFAFA',
          border: '1px solid #E0E0E0',
          borderRadius: 2,
          maxHeight: '400px',
          overflow: 'auto'
        }}
      >
        <Box
          component="pre"
          sx={{
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            fontSize: '13px',
            lineHeight: 1.6,
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            '& a': {
              color: '#1976D2',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }
          }}
          dangerouslySetInnerHTML={{ __html: formattedXml }}
        />
      </Paper>
    </Box>
  );
};

export default XmlResponseViewer;